'use client';

import type { SVGProps } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PIECES, ValeAILogo } from './vale-ai-logo';

/**
 * The Vale mark as the product's AI indicator, in place of the lucide sparkle
 * (VALE-1251). VALE-1221 made the mark the "Vale AI is working" glyph; this is
 * the same mark wearing the sparkle's job everywhere else.
 *
 * **Why a scale factor exists.** The sparkle is a stroke drawing that fills its
 * viewBox corner to corner. The mark is three filled shapes with air around
 * them, so a mark drawn into the sparkle's box reads noticeably smaller and
 * lighter. The multiple that carries the same optical weight was settled by eye
 * in the `Prototypes/Analysing Documents/Vale AI logo` Storybook page
 * (`Swap · glyph sizes`) at 1.30x, and that page ships as the record.
 *
 * **One factor, one definition.** No call site restates 1.30. A site says which
 * box its sparkle occupied and this module does the arithmetic, so re-judging
 * the scale later is a one-line change here rather than a 40-file sweep.
 *
 * **Two shapes, because the call sites come in two shapes.**
 *
 * - {@link ValeAIMark}: the site controls the glyph's box, so the box GROWS to
 *   1.30x and the surrounding layout reflows around it. This is the common case
 *   and the one the Storybook comparison was judged on.
 * - {@link ValeAIMarkIcon}: a `LucideIcon`-typed slot (`Banner`'s `icon`, the
 *   `FileDropZone` sell configs, the discovery status pills) hands the glyph a
 *   fixed box class it cannot grow past, so the mark is drawn 1.30x IN PLACE
 *   via a CSS transform and overflows its box. Layout is untouched.
 *
 * Both inherit `currentColor`, so every site keeps the colour token it sets
 * today and neither carries a fixed purple.
 */

/** The mark's box as a multiple of the box the lucide sparkle occupied. */
export const VALE_AI_MARK_SCALE = 1.3;

/** The box a mark takes over from a sparkle of `sparkleBox` px, in whole px. */
export function valeAIMarkBox(sparkleBox: number): number {
  return Math.round(sparkleBox * VALE_AI_MARK_SCALE);
}

export interface ValeAIMarkProps {
  /**
   * The px box the lucide sparkle occupied at this site (`h-3 w-3` is 12). The
   * mark is drawn at {@link valeAIMarkBox} of it, so pass the OLD box, not the
   * new one, so the scale stays owned by this module.
   */
  box: number;
  /** Pulse the three pieces in turn. Off = the plain solid mark. */
  thinking?: boolean;
  /** Accessible name. Without one the glyph is decorative and hidden from AT. */
  label?: string;
  /**
   * Colour, spacing and animation classes only, never `h-*`/`w-*`/`size-*`.
   * A size class would beat the width/height attributes and clamp the scale
   * back to the sparkle's box, silently undoing the whole point.
   */
  className?: string;
}

/** The mark at 1.30x the sparkle's box, in the site's own colour. */
export function ValeAIMark({
  box,
  thinking = false,
  label,
  className
}: ValeAIMarkProps) {
  return (
    <ValeAILogo
      size={valeAIMarkBox(box)}
      color="currentColor"
      thinking={thinking}
      label={label}
      className={className}
    />
  );
}

/**
 * Drop-in for `Sparkles` in a slot typed as a `LucideIcon`.
 *
 * The slot owns the box (`h-full w-full` inside `Banner`'s 24px chip,
 * `size-7` in the `FileDropZone` banner), so the mark fills that box and is
 * then scaled 1.30x about its centre. `overflow-visible` is what lets the
 * drawing exceed the box instead of being clipped by it.
 */
export const ValeAIMarkIcon = (({
  className,
  size,
  style,
  ...props
}: SVGProps<SVGSVGElement> & { size?: number | string }) => (
  <span
    className={cn('inline-grid shrink-0 place-items-center', className)}
    style={size == null ? undefined : { width: size, height: size }}
  >
    <svg
      viewBox="0 0 1500 1500"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className="h-full w-full overflow-visible"
      {...props}
      // `style` is pulled out of `props` and re-applied AFTER the spread, with
      // the transform last, so a caller's own style cannot silently drop the
      // 1.30x scale. The scale is this component's whole contract: without it
      // the mark renders at the sparkle's box and reads smaller and lighter,
      // which is the defect VALE-1251 exists to fix, and it would fail
      // silently at whichever call site passed the style.
      style={{ ...style, transform: `scale(${VALE_AI_MARK_SCALE})` }}
    >
      {PIECES.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  </span>
)) as unknown as LucideIcon;
